import React, { useEffect } from "react";
import { Container, Typography, Button } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { FaPencilAlt, FaUserEdit, FaHeadphones, FaChartLine } from "react-icons/fa";
import "aos/dist/aos.css";
import AOS from "aos";

const About = () => {
  const navigate = useNavigate();

  useEffect(() => {
    AOS.init({ duration: 1000 });
    AOS.refresh();
  }, []);

  return (
    <div
      style={{
        backgroundImage: `url(${require("../assets/images/landing_background.jpg")})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        minHeight: "100vh",
        fontFamily: "'Baloo Bhaijaan 2', cursive",
        color: "white",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
      }}
    >
      {/* Header (aligned with Landing/Contact) */}
      <header
        style={{
          width: "100%",
          padding: "10px 20px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: "rgba(0, 0, 0, 0.8)",
          boxShadow: "0px 2px 5px rgba(0, 0, 0, 0.5)",
          overflowX: "auto",
          whiteSpace: "nowrap",
        }}
      >
        <img
          src={require("../assets/images/logo_name.PNG")}
          alt="YourStoryWorld Logo"
          style={{
            height: "40px",
            cursor: "pointer",
            flexShrink: 0,
          }}
          onClick={() => navigate("/")}
        />
        <div
          style={{
            display: "inline-flex",
            gap: "15px",
            justifyContent: "flex-end",
            paddingRight: "10px",
            whiteSpace: "nowrap",
            flexShrink: 0,
          }}
        >
          <Button
            color="inherit"
            onClick={() => navigate("/")}
            style={headerButtonStyle}
            onMouseOver={(e) => {
              e.currentTarget.style.backgroundColor = "#1976D2";
              e.currentTarget.style.color = "white";
              e.currentTarget.style.border = "1px solid white";
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.backgroundColor = "transparent";
              e.currentTarget.style.color = "white";
              e.currentTarget.style.border = "1px solid transparent";
            }}
          >
            Home
          </Button>
          <Button
            color="inherit"
            onClick={() => navigate("/login")} // ✅ match rest of app
            style={headerButtonStyle}
            onMouseOver={(e) => {
              e.currentTarget.style.backgroundColor = "#1976D2";
              e.currentTarget.style.color = "white";
              e.currentTarget.style.border = "1px solid white";
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.backgroundColor = "transparent";
              e.currentTarget.style.color = "white";
              e.currentTarget.style.border = "1px solid transparent";
            }}
          >
            Sign In
          </Button>
          <Button
            color="inherit"
            onClick={() => navigate("/help")}
            style={headerButtonStyle}
            onMouseOver={(e) => {
              e.currentTarget.style.backgroundColor = "#D32F2F";
              e.currentTarget.style.color = "white";
              e.currentTarget.style.border = "1px solid white";
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.backgroundColor = "transparent";
              e.currentTarget.style.color = "white";
              e.currentTarget.style.border = "1px solid transparent";
            }}
          >
            Help
          </Button>
        </div>
      </header>

      {/* Content */}
      <Container
        maxWidth="lg"
        style={{
          marginTop: "30px",
          marginBottom: "30px",
        }}
      >
        {/* Introduction Section */}
        <section
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexDirection: "row-reverse",
            marginBottom: "40px",
            flexWrap: "wrap",
          }}
          data-aos="fade-right"
        >
          <div
            style={{
              flex: 1,
              paddingLeft: "20px",
              minWidth: "300px",
              marginBottom: "15px",
            }}
          >
            <Typography
              variant="h4"
              style={{ marginBottom: "20px", fontWeight: "bold" }}
            >
              About YourStoryWorld
            </Typography>
            <Typography>
              YourStoryWorld is a storytelling app that turns children into the
              heroes of their own adventures. Every story is generated
              on-demand, using the child’s name, friends, favourite places and
              themes they choose.
              <br />
              <br />
              Children simply pick a theme, add characters, and select a setting
              — YourStoryWorld instantly creates a personalised, age-appropriate
              story they can read or listen to.
              <br />
              <br />
              The app adapts language for different age groups, helping early
              readers build confidence while still keeping older children
              challenged and engaged.
            </Typography>
          </div>
          <img
            src={require("../assets/images/about_introduction.jpg")}
            alt="About Us"
            style={{
              flex: 1,
              maxWidth: "400px",
              width: "100%",
              borderRadius: "10px",
              margin: "10px 0",
            }}
            data-aos="fade-left"
          />
        </section>

        {/* Who it's for */}
        <section
          style={{
            marginBottom: "40px",
            marginTop: "10px",
          }}
          data-aos="fade-up"
        >
          <Typography
            variant="h4"
            style={{ marginBottom: "20px", fontWeight: "bold" }}
          >
            Who is YourStoryWorld for?
          </Typography>
          <Typography component="div">
            <ul style={{ paddingLeft: "18px" }}>
              <li>
                <strong>Families at home</strong> — Parents who want a fun,
                low-friction way to get their children reading more regularly.
              </li>
              <li>
                <strong>Schools & teachers</strong> — Classrooms that need
                curriculum-friendly stories, phonics support, and reading
                analytics.
              </li>
              <li>
                <strong>Reluctant or anxious readers</strong> — Children who
                engage more when they see themselves represented in the story.
              </li>
            </ul>
          </Typography>
        </section>

        {/* Features Section */}
        <section
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "40px",
            marginTop: "40px",
            flexWrap: "wrap",
          }}
          data-aos="fade-up"
          data-aos-delay="200"
        >
          <div
            style={{
              flex: 1,
              paddingRight: "20px",
              minWidth: "300px",
              marginBottom: "15px",
            }}
          >
            <Typography
              variant="h4"
              style={{ marginBottom: "20px", fontWeight: "bold" }}
            >
              Key Features
            </Typography>
            <ul style={{ listStyleType: "none", padding: 0 }}>
              <li
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: "10px",
                }}
              >
                <span style={{ marginRight: "10px" }}>
                  <FaPencilAlt size={20} color="#1976D2" />
                </span>
                Personalized story creation in under a minute.
              </li>
              <li
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: "10px",
                }}
              >
                <span style={{ marginRight: "10px" }}>
                  <FaUserEdit size={20} color="#D32F2F" />
                </span>
                Customisable characters, settings, and themes.
              </li>
              <li
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: "10px",
                }}
              >
                <span style={{ marginRight: "10px" }}>
                  <FaHeadphones size={20} color="#388E3C" />
                </span>
                Audio playback for bedtime or independent listening.
              </li>
              <li
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: "10px",
                }}
              >
                <span style={{ marginRight: "10px" }}>
                  <FaChartLine size={20} color="#FFC107" />
                </span>
                Progress tracking and a growing story library.
              </li>
            </ul>
          </div>
          <img
            src={require("../assets/images/about_features.jpg")}
            alt="Features"
            style={{
              flex: 1,
              maxWidth: "400px",
              width: "100%",
              borderRadius: "10px",
              margin: "10px 0",
            }}
          />
        </section>

        {/* For Schools & Teachers */}
        <section
          style={{
            marginBottom: "40px",
          }}
          data-aos="fade-up"
        >
          <Typography
            variant="h4"
            style={{ marginBottom: "20px", fontWeight: "bold" }}
          >
            Built for Classrooms Too
          </Typography>
          <Typography component="div">
            YourStoryWorld isn’t just a family app — it’s designed with schools
            in mind:
            <ul style={{ paddingLeft: "18px", marginTop: "10px" }}>
              <li>Curriculum-aligned vocabulary and phonics-friendly stories.</li>
              <li>
                Teacher-assigned themes so stories match current topics (e.g.
                space, habitats, history).
              </li>
              <li>
                Comprehension questions and quiz scores to check understanding.
              </li>
              <li>
                Student voice recordings and analytics to track engagement and
                progress over time.
              </li>
            </ul>
          </Typography>
          <Button
            variant="contained"
            style={{
              marginTop: "15px",
              backgroundColor: "#4CAF50",
              color: "white",
              fontWeight: "bold",
            }}
            onClick={() => navigate("/schools")}
          >
            Learn more about School plans
          </Button>
        </section>

        {/* Mission Section */}
        <section
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexDirection: "row-reverse",
            marginBottom: "40px",
            flexWrap: "wrap",
          }}
          data-aos="fade-right"
        >
          <div
            style={{
              flex: 1,
              paddingLeft: "20px",
              minWidth: "300px",
              marginBottom: "15px",
            }}
          >
            <Typography
              variant="h4"
              style={{ marginBottom: "20px", fontWeight: "bold" }}
            >
              Our Mission
            </Typography>
            <Typography>
              At YourStoryWorld, we’re passionate about nurturing creativity and
              a love of reading in children from all backgrounds.
              <br />
              <br />
              Research shows that reading for pleasure is one of the strongest
              predictors of future success. We want to make that reading
              experience:
              <br />
              • Personal — so children see themselves in the story. <br />
              • Accessible — so families and schools can use it with minimal
              setup. <br />
              • Joyful — so “one more chapter” becomes a daily habit, not a
              battle.
            </Typography>
          </div>
          <img
            src={require("../assets/images/about_mission.jpg")}
            alt="Mission"
            style={{
              flex: 1,
              maxWidth: "400px",
              width: "100%",
              borderRadius: "10px",
              margin: "10px 0",
            }}
            data-aos="fade-left"
          />
        </section>

        {/* Founders' Story / Why we built it — with illustration */}
<section
  style={{
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    marginBottom: "60px",
  }}
  data-aos="fade-up"
>
  {/* Illustration */}
  <div
    style={{
      flex: 1,
      minWidth: "260px",
      textAlign: "center",
      marginBottom: "20px",
    }}
  >
    <img
      src={require("../assets/images/founder_story.png")} 
      alt="Founder's Story Illustration"
      style={{
        width: "80%",
        maxWidth: "280px",
        borderRadius: "12px",
      }}
    />
  </div>

  {/* Text Section */}
  <div
    style={{
      flex: 2,
      minWidth: "300px",
      paddingLeft: "20px",
      color: "white",
    }}
  >
    <Typography
      variant="h4"
      style={{ marginBottom: "20px", fontWeight: "bold" }}
    >
      A Note from the Founders
    </Typography>

    <Typography>
      YourStoryWorld began as a personal mission right here in Birmingham, UK.
      As parents ourselves, we found it increasingly difficult to get our own
      children — especially my 7-year-old daughter — to pick up a book and read.
      She loved being on her iPad, but traditional books just weren’t capturing
      her imagination.
      <br /><br />
      We realised something important: it wasn’t that she didn’t want to read —
      she just wasn’t excited by the stories in front of her. She wanted something
      more modern, more interactive, and something that felt like <i>her</i>.
      Stories where she and her friends were the main characters.
      <br /><br />
      So we built a tool that could:
      <br />
      • Put each child directly into the story with their friends and favourite places.<br />
      • Match the reading level to their age to build confidence.<br />
      • Bring reading into the digital world in a way that encourages learning.<br />
      • Reduce friction for busy parents and teachers while inspiring children to read daily.<br />
      <br />
      YourStoryWorld is built in the UK with diverse families, schools, and communities
      in mind. Every update is inspired by real feedback from parents, teachers, and early
      readers. Our mission is simple: help children fall in love with reading by making
      every story feel like an adventure written just for them.
    </Typography>
  </div>
</section>


        {/* Privacy & Safety Section */}
        <section style={{ marginBottom: "40px" }} data-aos="fade-up">
          <Typography
            variant="h4"
            style={{ marginBottom: "20px", fontWeight: "bold" }}
          >
            Privacy & Safety
          </Typography>
          <Typography>
            We know how important it is to keep children safe online. Stories
            are generated securely, and we focus on age-appropriate, positive,
            and inclusive content.
            <br />
            <br />
            We don’t sell your data, and we’re continually improving our systems
            to keep YourStoryWorld a safe space for families and schools.
          </Typography>
        </section>

        {/* Contact Section */}
        <section style={{ marginBottom: "40px" }} data-aos="fade-up">
          <Typography
            variant="h4"
            style={{ marginBottom: "20px", fontWeight: "bold" }}
          >
            Contact Us
          </Typography>
          <Typography>
            Have questions, feedback, or ideas? Reach out to us at{" "}
            <a
              href="mailto:support@yourstoryworld.com"
              style={{ color: "white", textDecoration: "underline" }}
            >
              support@yourstoryworld.com
            </a>
            . We’d love to hear from you.
          </Typography>
        </section>

        {/* Call to action */}
        <section
          style={{
            marginBottom: "20px",
            textAlign: "center",
          }}
          data-aos="zoom-in"
        >
          <Typography
            variant="h5"
            style={{ marginBottom: "15px", fontWeight: "bold" }}
          >
            Ready to start their next adventure?
          </Typography>
          <div>
            <Button
              variant="contained"
              style={{
                marginRight: "10px",
                backgroundColor: "#1976D2",
                color: "white",
                fontWeight: "bold",
              }}
              onClick={() => navigate("/subscriptions")}
            >
              View Plans
            </Button>
            <Button
              variant="outlined"
              style={{
                borderColor: "white",
                color: "white",
                fontWeight: "bold",
              }}
              onClick={() => navigate("/login")}
            >
              Sign In
            </Button>
          </div>
        </section>
      </Container>

      {/* Footer */}
      <footer
        style={{
          textAlign: "center",
          padding: "20px",
          backgroundColor: "#000",
          color: "#fff",
        }}
      >
        {/* Company & rights */}
        <Typography variant="body2">
          © {new Date().getFullYear()} YourStoryWorld Ltd  
          · Company No. 17098564 · All rights reserved.
        </Typography>

        {/* Main nav links */}
        <Typography variant="body2" style={{ marginTop: "8px" }}>
          <a href="/about" style={footerLinkStyle}>
            About Us
          </a>{" "}
          |{" "}
          <a href="/contact" style={footerLinkStyle}>
            Contact
          </a>{" "}
          |{" "}
          <a href="/help" style={footerLinkStyle}>
            Help & FAQs
          </a>
        </Typography>

        {/* Policy links */}
        <Typography variant="body2" style={{ marginTop: 4 }}>
  <a href="/privacy-policy" style={footerLinkStyle}>Privacy Policy</a> |{" "}
  <a href="/terms" style={footerLinkStyle}>Terms of Use</a> |{" "}
  <a href="/cookie-policy" style={footerLinkStyle}>Cookie Policy</a> |{" "}
  <a href="/child-safety" style={footerLinkStyle}>Child Safety</a> |{" "}
  <a href="/accessibility" style={footerLinkStyle}>Accessibility</a> |{" "}
  <a href="/data-protection" style={footerLinkStyle}>Data Protection &amp; Security</a>
</Typography>

        {/* Disclaimer */}
        <Typography
          variant="caption"
          style={{ marginTop: "10px", display: "block", color: "#ccc" }}
        >
         ⚠️ Disclaimer: The stories generated by YourStoryWorld are created using
          AI and are intended for entertainment and educational purposes only.
          While we strive for accuracy, some content may contain factual or
          narrative inconsistencies.
          <p style={{ fontSize: "0.8rem", color: "#e11d48", marginTop: "10px" }}>
  <strong>Child-Safe Content:</strong> YourStoryWorld uses AI safety filters to ensure all stories 
  remain age-appropriate. Violent, explicit, discriminatory, or harmful content is automatically 
  removed or rewritten. Minor variance may occur due to the nature of AI generation.
</p>

        </Typography>
      </footer>
    </div>
  );
};

const headerButtonStyle = {
  fontSize: "16px",
  fontWeight: "bold",
  textTransform: "uppercase",
  color: "white",
  border: "1px solid transparent",
  transition: "all 0.3s ease",
};


const footerLinkStyle = {
  color: "#fff",
  textDecoration: "none",
  margin: "0 4px",
};
  

export default About;
